import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, ClipboardList, LayoutDashboard, Search, Trash2, Pencil, Plus, X, Loader2, ChevronLeft, ChevronRight, Inbox, Download, FileText, Wallet, Mail, MessageCircle, ImagePlus, Settings, AlertTriangle, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProductImage } from "@/components/ProductImage";
import { LOGO, fmt } from "@/lib/site";

const STATUSES = ["pendiente", "confirmado", "por_verificar", "pagado", "despachado", "entregado", "cancelado"];
const PROJ_STATUSES = ["nuevo", "en_proceso", "cotizado", "cerrado", "cancelado"];
const QUOTE_STATUSES = ["nuevo", "cotizado", "enviado", "cerrado", "cancelado"];

function QuoteCard({ q, onChange }) {
    const [lineas, setLineas] = useState(q.lineas?.length ? q.lineas : [{ descripcion: "", cantidad: 1, precio_unitario: 0 }]);
    const [validez, setValidez] = useState(q.validez_dias ?? 15);
    const [respuesta, setRespuesta] = useState(q.respuesta ?? "");
    const [busy, setBusy] = useState(false);
    const total = lineas.reduce((s, l) => s + (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0), 0);
    const token = localStorage.getItem("lr_token");

    const upd = (i, f, v) => setLineas((ls) => ls.map((l, idx) => (idx === i ? { ...l, [f]: v } : l)));
    const addLine = () => setLineas((ls) => [...ls, { descripcion: "", cantidad: 1, precio_unitario: 0 }]);
    const delLine = (i) => setLineas((ls) => ls.filter((_, idx) => idx !== i));

    const persist = async () => {
        await api.patch(`/quotes/${q.id}`, {
            lineas: lineas.map((l) => ({ descripcion: l.descripcion, cantidad: parseFloat(l.cantidad) || 0, precio_unitario: parseFloat(l.precio_unitario) || 0 })),
            validez_dias: parseInt(validez) || 15,
            respuesta,
        });
    };
    const save = async () => { setBusy(true); try { await persist(); toast.success("Cotización guardada"); onChange && onChange(); } catch { toast.error("Error al guardar"); } finally { setBusy(false); } };
    const setStatus = async (e) => { await api.patch(`/quotes/${q.id}`, { estado: e.target.value }); onChange && onChange(); };

    const sendEmail = async () => {
        if (!q.email) return toast.error("El cliente no tiene correo registrado");
        setBusy(true);
        try { await persist(); await api.post(`/quotes/${q.id}/send-email`); toast.success("Cotización enviada al correo del cliente"); onChange && onChange(); }
        catch (e) { toast.error("No se pudo enviar", { description: e?.response?.data?.detail }); }
        finally { setBusy(false); }
    };
    const sendWhatsApp = async () => {
        setBusy(true);
        try {
            await persist();
            const { data } = await api.get(`/quotes/${q.id}/share`);
            let tel = (q.telefono || "").replace(/\D/g, "");
            if (tel.startsWith("0")) tel = "58" + tel.slice(1);
            const msg = encodeURIComponent(`Hola ${q.nombre}, aquí tu cotización ${q.codigo} de Maderas La Romanilla (total $${total.toFixed(2)}): ${data.url}`);
            window.open(tel ? `https://wa.me/${tel}?text=${msg}` : `https://wa.me/?text=${msg}`, "_blank");
            onChange && onChange();
        } catch { toast.error("No se pudo generar el enlace"); }
        finally { setBusy(false); }
    };

    const inp = "bg-crema border border-linea focus:border-madera rounded-lg outline-none py-2 px-2.5 text-sm";
    return (
        <div data-testid={`quote-${q.codigo}`} className="border border-linea rounded-2xl bg-hueso p-5 shadow-soft">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                    <span className="text-sm font-bold">{q.codigo}</span>
                    <p className="text-xs text-mut">{q.nombre} · {q.telefono}{q.email ? " · " + q.email : ""} · {q.tipo}</p>
                    {(q.producto || q.contacto_preferencia) && (
                        <p className="text-xs text-madera-dark mt-0.5">
                            {q.producto ? <>🪵 {q.producto}{q.producto_sku ? ` (${q.producto_sku})` : ""}{q.producto_precio ? ` · ${q.producto_precio}` : ""}</> : null}
                            {q.contacto_preferencia ? <span className="ml-2">· prefiere: <b>{q.contacto_preferencia}</b></span> : null}
                        </p>
                    )}
                </div>
                <select value={q.estado} onChange={setStatus} className="text-[11px] uppercase px-3 py-1.5 rounded-full border border-linea bg-white outline-none">
                    {QUOTE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
            <p className="text-xs text-mut border-t border-linea pt-3 whitespace-pre-wrap mb-4"><b>Pedido:</b> {q.detalle}</p>

            <div className="space-y-2">
                {lineas.map((l, i) => (
                    <div key={i} className="flex gap-2 items-center">
                        <input className={`${inp} flex-1`} placeholder="Descripción" value={l.descripcion} onChange={(e) => upd(i, "descripcion", e.target.value)} data-testid={`ql-desc-${i}`} />
                        <input className={`${inp} w-16 text-right`} type="number" placeholder="Cant" value={l.cantidad} onChange={(e) => upd(i, "cantidad", e.target.value)} data-testid={`ql-cant-${i}`} />
                        <input className={`${inp} w-24 text-right`} type="number" step="0.01" placeholder="P.Unit" value={l.precio_unitario} onChange={(e) => upd(i, "precio_unitario", e.target.value)} data-testid={`ql-precio-${i}`} />
                        <span className="w-24 text-right text-sm font-medium">{fmt((parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0))}</span>
                        <button onClick={() => delLine(i)} className="text-mut hover:text-destructive p-1"><Trash2 size={15} /></button>
                    </div>
                ))}
            </div>
            <button onClick={addLine} data-testid={`ql-add-${q.codigo}`} className="mt-2 inline-flex items-center gap-1.5 text-sm text-verde font-medium hover:text-naranja"><Plus size={15} /> Agregar ítem</button>

            <div className="flex flex-wrap items-center justify-between gap-3 mt-4 border-t border-linea pt-4">
                <div className="flex items-center gap-2">
                    <label className="text-xs text-mut">Validez (días)</label>
                    <input className={`${inp} w-20`} type="number" value={validez} onChange={(e) => setValidez(e.target.value)} data-testid={`ql-validez-${q.codigo}`} />
                </div>
                <div className="text-right">
                    <span className="text-xs uppercase tracking-widest text-mut">Total</span>
                    <p className="font-display font-extrabold text-2xl text-verde">{fmt(total)}</p>
                </div>
            </div>
            <input className={`${inp} w-full mt-3`} placeholder="Nota / propuesta para el cliente" value={respuesta} onChange={(e) => setRespuesta(e.target.value)} data-testid={`ql-nota-${q.codigo}`} />

            <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={save} disabled={busy} data-testid={`quote-save-${q.codigo}`} className="bg-verde text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-verde-800 transition-colors disabled:opacity-60">Guardar</button>
                <a href={`${API}/quotes/${q.id}/pdf?auth=${token}`} target="_blank" rel="noreferrer" data-testid={`quote-pdf-${q.codigo}`} className="inline-flex items-center gap-2 border border-verde text-verde rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-verde hover:text-white transition-colors"><FileText size={15} /> PDF</a>
                <button onClick={sendEmail} disabled={busy} data-testid={`quote-email-${q.codigo}`} className="inline-flex items-center gap-2 border border-madera text-madera-dark rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-madera hover:text-white transition-colors disabled:opacity-60"><Mail size={15} /> Enviar correo</button>
                <button onClick={sendWhatsApp} disabled={busy} data-testid={`quote-wa-${q.codigo}`} className="inline-flex items-center gap-2 border border-[#25D366] text-[#128C4B] rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#25D366] hover:text-white transition-colors disabled:opacity-60"><MessageCircle size={15} /> WhatsApp</button>
            </div>
        </div>
    );
}
const PAY_LABELS = { pago_movil: "Pago Móvil", zelle: "Zelle", binance: "Binance Pay", transferencia: "Transferencia", efectivo: "Efectivo en tienda" };
const PAY_ORDER = ["pago_movil", "zelle", "binance", "transferencia", "efectivo"];
const STATUS_COLOR = {
    pendiente: "bg-amber-100 text-amber-800",
    confirmado: "bg-blue-100 text-blue-800",
    por_verificar: "bg-orange-100 text-orange-800",
    pagado: "bg-emerald-100 text-emerald-800",
    despachado: "bg-indigo-100 text-indigo-800",
    entregado: "bg-verde text-white",
    cancelado: "bg-red-100 text-red-700",
};

const emptyProduct = { sku: "", nombre: "", categoria: "", subcategoria: "", marca: "Generica", precio_usd: 0, stock: 999, foto_url: "", fotos: [], descripcion: "", unidad_venta: "", rendimiento: "", etiqueta: "", destacado: false, activo: true };

function ProductModal({ product, onClose, onSaved }) {
    const [form, setForm] = useState(product || emptyProduct);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const uploadImages = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        setUploading(true);
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const { data } = await api.post(`/products/${form.id}/images`, fd);
            setForm((f) => ({ ...f, fotos: data.fotos || [] }));
            toast.success(`${files.length} foto(s) agregada(s)`);
        } catch (err) {
            toast.error("Error al subir fotos", { description: err?.response?.data?.detail });
        } finally {
            setUploading(false);
        }
    };

    const removeImage = async (url) => {
        try {
            const { data } = await api.delete(`/products/${form.id}/images`, { data: { url } });
            setForm((f) => ({ ...f, fotos: data.fotos || [] }));
            toast.success("Foto eliminada");
        } catch (err) {
            toast.error("No se pudo eliminar la foto");
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form, precio_usd: parseFloat(form.precio_usd) || 0, stock: parseInt(form.stock) || 0 };
            if (form.id) await api.put(`/products/${form.id}`, payload);
            else await api.post("/products", payload);
            toast.success("Producto guardado");
            onSaved();
            onClose();
        } catch (e) {
            toast.error("Error al guardar", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    const cls = "w-full bg-crema border border-linea focus:border-madera rounded-lg outline-none py-2.5 px-3 text-sm";
    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-verde-900/70" onClick={onClose} />
            <div className="relative bg-hueso rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 md:p-8" data-testid="product-modal">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="font-display font-bold text-2xl">{form.id ? "Editar" : "Nuevo"} producto</h3>
                    <button onClick={onClose}><X size={22} /></button>
                </div>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <input className={cls} placeholder="SKU" value={form.sku} onChange={set("sku")} data-testid="pm-sku" />
                        <input className={cls} placeholder="Marca" value={form.marca} onChange={set("marca")} />
                    </div>
                    <input className={cls} placeholder="Nombre" value={form.nombre} onChange={set("nombre")} data-testid="pm-nombre" />
                    <div className="grid grid-cols-2 gap-3">
                        <input className={cls} placeholder="Categoría" value={form.categoria} onChange={set("categoria")} data-testid="pm-categoria" />
                        <input className={cls} placeholder="Subcategoría" value={form.subcategoria} onChange={set("subcategoria")} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <input className={cls} type="number" step="0.01" placeholder="Precio USD" value={form.precio_usd} onChange={set("precio_usd")} data-testid="pm-precio" />
                        <input className={cls} type="number" placeholder="Stock" value={form.stock} onChange={set("stock")} />
                    </div>
                    <input className={cls} placeholder="URL de foto principal" value={form.foto_url} onChange={set("foto_url")} />
                    {/* Cómo se vende y cuánto rinde: es lo que el cliente pregunta
                        por WhatsApp cuando no está escrito */}
                    <input className={cls} placeholder="Unidad de venta (ej: Paquete de 100 unidades · Rollo de 5 m · Galón)" value={form.unidad_venta || ""} onChange={set("unidad_venta")} data-testid="prod-unidad" />
                    <input className={cls} placeholder="Rendimiento (ej: Rinde 12 m² · Alcanza para 3 puertas)" value={form.rendimiento || ""} onChange={set("rendimiento")} data-testid="prod-rendimiento" />
                    <textarea className={`${cls} min-h-20`} placeholder="Descripción" value={form.descripcion} onChange={set("descripcion")} />

                    {/* Galería de fotos */}
                    <div className="pt-1">
                        <label className="text-xs uppercase tracking-wide text-mut font-semibold">Galería de fotos</label>
                        {!form.id ? (
                            <p className="text-xs text-mut mt-1.5">Guarda el producto primero para poder subir fotos.</p>
                        ) : (
                            <>
                                <div className="grid grid-cols-4 gap-2 mt-2" data-testid="pm-gallery">
                                    {(form.fotos || []).map((url) => (
                                        <div key={url} className="relative aspect-square bg-crema border border-linea rounded-lg overflow-hidden group">
                                            <ProductImage src={url} alt="" className="w-full h-full object-contain p-1" />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(url)}
                                                data-testid="pm-gallery-remove"
                                                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 text-destructive flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    ))}
                                    <label
                                        data-testid="pm-gallery-upload-label"
                                        className={`aspect-square border-2 border-dashed border-linea rounded-lg flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-madera hover:bg-crema transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                                    >
                                        {uploading ? <Loader2 size={20} className="animate-spin text-madera" /> : <ImagePlus size={20} className="text-madera" />}
                                        <span className="text-[10px] text-mut">Subir</span>
                                        <input type="file" accept="image/*" multiple onChange={uploadImages} data-testid="pm-gallery-input" className="hidden" />
                                    </label>
                                </div>
                                <p className="text-[11px] text-mut mt-1.5">JPG, PNG o WEBP · máx 10 MB c/u. Se muestran como miniaturas en la vista del producto.</p>
                            </>
                        )}
                    </div>

                    <input className={cls} placeholder='Insignia en tienda (ej: BEST SELLER) — vacío = ninguna' value={form.etiqueta || ""} onChange={set("etiqueta")} data-testid="pm-etiqueta" />
                    <div className="flex gap-6 pt-1">
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.destacado} onChange={set("destacado")} /> Destacado</label>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.activo} onChange={set("activo")} /> Activo</label>
                    </div>
                </div>
                <button onClick={save} disabled={saving} data-testid="pm-save" className="w-full mt-6 bg-verde text-white rounded-full py-3.5 font-semibold hover:bg-verde-800 transition-colors flex items-center justify-center gap-2">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : "Guardar"}
                </button>
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const nav = useNavigate();
    const [tab, setTab] = useState("resumen");
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [comprobantes, setComprobantes] = useState(null); // {order, files} del modal
    const [projects, setProjects] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [payments, setPayments] = useState(null);
    const [savingPay, setSavingPay] = useState(false);
    const [general, setGeneral] = useState(null);
    const [savingGen, setSavingGen] = useState(false);
    const [prodData, setProdData] = useState({ items: [], total: 0, page: 1, pages: 1 });
    const [q, setQ] = useState("");
    const [pPage, setPPage] = useState(1);
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        if (!loading && !user) nav("/padmin");
    }, [loading, user, nav]);

    const loadStats = useCallback(() => api.get("/stats").then((r) => setStats(r.data)).catch(() => {}), []);
    const loadOrders = useCallback(() => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}), []);
    const loadProjects = useCallback(() => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}), []);
    const loadQuotes = useCallback(() => api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {}), []);
    const loadPayments = useCallback(() => api.get("/settings/payments").then((r) => setPayments(r.data)).catch(() => {}), []);
    const loadGeneral = useCallback(() => api.get("/settings/general").then((r) => setGeneral(r.data)).catch(() => {}), []);
    const loadProducts = useCallback(() => {
        api.get("/products", { params: { search: q || undefined, page: pPage, limit: 20 } }).then((r) => setProdData(r.data)).catch(() => {});
    }, [q, pPage]);

    useEffect(() => { if (user) { loadStats(); loadOrders(); loadProjects(); loadPayments(); loadQuotes(); loadGeneral(); } }, [user, loadStats, loadOrders, loadProjects, loadPayments, loadQuotes, loadGeneral]);
    useEffect(() => { if (user && tab === "productos") loadProducts(); }, [user, tab, loadProducts]);

    const verComprobantes = async (o) => {
        try {
            const { data } = await api.get(`/orders/${o.id}/comprobantes`);
            if (!data.length) return toast.info("Este pedido aún no tiene comprobantes");
            setComprobantes({ order: o, files: data });
        } catch {
            toast.error("No se pudieron cargar los comprobantes");
        }
    };

    const setStatus = async (id, estado) => {
        await api.patch(`/orders/${id}`, { estado });
        toast.success("Estado actualizado");
        loadOrders();
        loadStats();
    };

    const setProjStatus = async (id, estado) => {
        await api.patch(`/projects/${id}`, { estado });
        toast.success("Estado actualizado");
        loadProjects();
    };

    const savePayments = async () => {
        setSavingPay(true);
        try {
            await api.put("/settings/payments", payments);
            toast.success("Datos de pago guardados");
        } catch {
            toast.error("No se pudo guardar");
        } finally {
            setSavingPay(false);
        }
    };

    const saveGeneral = async () => {
        setSavingGen(true);
        try {
            await api.put("/settings/general", general);
            toast.success("Configuración guardada");
        } catch {
            toast.error("No se pudo guardar");
        } finally {
            setSavingGen(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("¿Eliminar este producto?")) return;
        await api.delete(`/products/${id}`);
        toast.success("Producto eliminado");
        loadProducts();
    };

    if (loading || !user) return <div className="min-h-screen hero-gradient flex items-center justify-center text-white">Cargando…</div>;

    const NAV = [
        { id: "resumen", label: "Resumen", icon: LayoutDashboard },
        { id: "pedidos", label: "Pedidos", icon: ClipboardList },
        { id: "proyectos", label: "Proyectos", icon: Inbox },
        { id: "presupuestos", label: "Presupuestos", icon: FileText },
        { id: "pagos", label: "Pagos", icon: Wallet },
        { id: "config", label: "Configuración", icon: Settings },
        { id: "productos", label: "Productos", icon: Package },
    ];

    return (
        <div className="min-h-screen bg-crema flex">
            <aside className="w-16 md:w-64 hero-gradient text-white flex flex-col shrink-0 sticky top-0 h-screen">
                <div className="p-4 md:p-6 border-b border-white/10">
                    <img src={LOGO.white} alt="" className="h-8 hidden md:block" />
                    <span className="md:hidden font-display font-extrabold text-madera text-2xl">LR</span>
                </div>
                <nav className="flex-1 p-2 md:p-4 space-y-1">
                    {NAV.map((n) => (
                        <button key={n.id} onClick={() => setTab(n.id)} data-testid={`admin-tab-${n.id}`} className={`w-full flex items-center gap-3 px-3 md:px-4 py-3 rounded-lg text-sm font-medium transition-colors ${tab === n.id ? "bg-naranja text-white" : "text-white/70 hover:text-white hover:bg-white/5"}`}>
                            <n.icon size={18} /> <span className="hidden md:inline">{n.label}</span>
                        </button>
                    ))}
                </nav>
                {/* Un solo enlace: todos los documentos viven en /docs */}
                <a href="/docs" target="_blank" rel="noopener" data-testid="admin-docs" className="mx-2 md:mx-4 flex items-center gap-3 px-3 md:px-4 py-3 rounded-lg text-sm font-medium text-madera hover:text-white hover:bg-white/5 transition-colors">
                    <BookOpen size={18} /> <span className="hidden md:inline">Documentos</span>
                </a>
                <button onClick={() => { logout(); nav("/padmin"); }} data-testid="admin-logout" className="m-2 md:m-4 flex items-center gap-3 px-3 md:px-4 py-3 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-colors">
                    <LogOut size={18} /> <span className="hidden md:inline">Salir</span>
                </button>
            </aside>

            <main className="flex-1 min-w-0 p-5 md:p-10">
                {tab === "resumen" && (
                    <div>
                        <h1 className="font-display font-extrabold text-3xl md:text-5xl text-ink tracking-tight mb-8">Resumen</h1>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { l: "Productos", v: stats?.productos ?? "—" },
                                { l: "Pedidos", v: stats?.pedidos ?? "—" },
                                { l: "Pendientes", v: stats?.pendientes ?? "—" },
                                { l: "Ingresos", v: stats ? fmt(stats.ingresos) : "—" },
                            ].map((s) => (
                                <div key={s.l} className="bg-hueso border border-linea rounded-2xl p-6 shadow-soft">
                                    <p className="text-xs uppercase tracking-widest text-mut">{s.l}</p>
                                    <p className="font-display font-extrabold text-3xl md:text-4xl text-verde mt-2">{s.v}</p>
                                </div>
                            ))}
                        </div>
                        <h2 className="font-display font-bold text-2xl text-ink mt-12 mb-4">Últimos pedidos</h2>
                        <div className="border border-linea rounded-2xl bg-hueso divide-y divide-linea overflow-hidden">
                            {orders.slice(0, 5).map((o) => (
                                <div key={o.id} className="p-4 flex items-center justify-between gap-4">
                                    <div><span className="text-sm font-semibold">{o.order_number}</span><p className="text-xs text-mut">{o.cliente?.nombre}</p></div>
                                    <span className="text-sm font-medium">{fmt(o.total)}</span>
                                    <span className={`text-[10px] uppercase px-2 py-1 rounded-full ${STATUS_COLOR[o.estado]}`}>{o.estado}</span>
                                </div>
                            ))}
                            {orders.length === 0 && <p className="p-6 text-sm text-mut">Aún no hay pedidos.</p>}
                        </div>
                    </div>
                )}

                {tab === "pedidos" && (
                    <div>
                        <h1 className="font-display font-extrabold text-3xl md:text-5xl text-ink tracking-tight mb-8">Pedidos</h1>
                        <div className="space-y-4">
                            {orders.map((o) => (
                                <div key={o.id} data-testid={`order-${o.order_number}`} className="border border-linea rounded-2xl bg-hueso p-5 shadow-soft">
                                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                        <div>
                                            <span className="text-sm font-bold">{o.order_number}</span>
                                            <p className="text-xs text-mut">{o.cliente?.nombre} · {o.cliente?.telefono} · {o.cliente?.tipo}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-display font-bold text-xl text-verde">{fmt(o.total)}</span>
                                            <select value={o.estado} onChange={(e) => setStatus(o.id, e.target.value)} data-testid={`order-status-${o.order_number}`} className={`text-[11px] uppercase px-3 py-1.5 rounded-full border-0 outline-none ${STATUS_COLOR[o.estado]}`}>
                                                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="text-xs text-mut border-t border-linea pt-3">
                                        {o.items?.map((i) => `${i.cantidad}× ${i.nombre}`).join("  ·  ")}
                                    </div>
                                    <div className="text-xs text-mut mt-2 space-y-0.5">
                                        <p><b className="text-ink">Pago:</b> {PAY_LABELS[o.metodo_pago] || o.metodo_pago}</p>
                                        {o.cliente?.entrega === "retiro"
                                            ? <p className="font-semibold text-naranja">🏪 RETIRA EN TIENDA</p>
                                            : o.cliente?.direccion && <p><b className="text-ink">Envío:</b> {o.cliente.direccion}{o.cliente.ciudad ? ` · ${o.cliente.ciudad}` : ""}</p>}
                                        {o.notas && <p className="whitespace-pre-line"><b className="text-ink">Notas:</b> {o.notas}</p>}
                                    </div>
                                    {/* Acciones de despacho: comprobantes del cliente, nota de pedido y contacto */}
                                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-linea">
                                        <button onClick={() => verComprobantes(o)} data-testid={`comprobantes-${o.order_number}`}
                                            className="flex items-center gap-1.5 text-xs font-semibold border border-linea rounded-full px-3 py-1.5 hover:border-verde hover:text-verde transition-colors">
                                            <FileText size={13} /> Comprobantes
                                        </button>
                                        <a href={`${API}/orders/${o.id}/pdf?auth=${localStorage.getItem("lr_token")}`} target="_blank" rel="noreferrer"
                                            data-testid={`pdf-${o.order_number}`}
                                            className="flex items-center gap-1.5 text-xs font-semibold border border-linea rounded-full px-3 py-1.5 hover:border-verde hover:text-verde transition-colors">
                                            <Download size={13} /> Nota de pedido
                                        </a>
                                        {o.cliente?.telefono && (
                                            <a href={`https://wa.me/${o.cliente.telefono.replace(/\D/g, "").replace(/^0/, "58")}?text=${encodeURIComponent(`Hola ${o.cliente.nombre}, te escribo de Maderas La Romanilla por tu pedido ${o.order_number}.`)}`}
                                                target="_blank" rel="noreferrer" data-testid={`wa-${o.order_number}`}
                                                className="flex items-center gap-1.5 text-xs font-semibold border border-linea rounded-full px-3 py-1.5 hover:border-verde hover:text-verde transition-colors">
                                                <MessageCircle size={13} /> WhatsApp al cliente
                                            </a>
                                        )}
                                        <a href={`${window.location.origin}/pedido/${o.order_number}`} target="_blank" rel="noreferrer"
                                            className="flex items-center gap-1.5 text-xs font-semibold border border-linea rounded-full px-3 py-1.5 hover:border-verde hover:text-verde transition-colors">
                                            Ver como el cliente
                                        </a>
                                    </div>
                                </div>
                            ))}
                            {orders.length === 0 && <p className="text-sm text-mut">Aún no hay pedidos.</p>}
                        </div>
                    </div>
                )}

                {tab === "proyectos" && (
                    <div>
                        <h1 className="font-display font-extrabold text-3xl md:text-5xl text-ink tracking-tight mb-8">Proyectos</h1>
                        <div className="space-y-4">
                            {projects.map((p) => (
                                <div key={p.id} data-testid={`project-${p.codigo}`} className="border border-linea rounded-2xl bg-hueso p-5 shadow-soft">
                                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                        <div>
                                            <span className="text-sm font-bold">{p.codigo}</span>
                                            <p className="text-xs text-mut">{p.nombre} · {p.telefono}{p.ciudad ? " · " + p.ciudad : ""}</p>
                                            <p className="text-xs text-madera-dark font-medium mt-0.5">{p.tipo_proyecto}</p>
                                        </div>
                                        <select value={p.estado} onChange={(e) => setProjStatus(p.id, e.target.value)} className="text-[11px] uppercase px-3 py-1.5 rounded-full border border-linea bg-white outline-none">
                                            {PROJ_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    {p.descripcion && <p className="text-sm text-ink/80 border-t border-linea pt-3">{p.descripcion}</p>}
                                    {p.archivo && (
                                        <a
                                            href={`${API}/projects/file/${p.archivo.storage_path}?auth=${localStorage.getItem("lr_token")}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`project-file-${p.codigo}`}
                                            className="mt-3 inline-flex items-center gap-2 text-sm text-verde font-medium hover:text-naranja transition-colors"
                                        >
                                            <FileText size={16} /> {p.archivo.original_filename} <Download size={14} />
                                        </a>
                                    )}
                                </div>
                            ))}
                            {projects.length === 0 && <p className="text-sm text-mut">Aún no hay proyectos enviados.</p>}
                        </div>
                    </div>
                )}

                {tab === "presupuestos" && (
                    <div>
                        <h1 className="font-display font-extrabold text-3xl md:text-5xl text-ink tracking-tight mb-8">Presupuestos</h1>
                        <div className="space-y-4">
                            {quotes.map((q) => <QuoteCard key={q.id} q={q} onChange={loadQuotes} />)}
                            {quotes.length === 0 && <p className="text-sm text-mut">Aún no hay solicitudes de presupuesto.</p>}
                        </div>
                    </div>
                )}

                {tab === "pagos" && (
                    <div>
                        <h1 className="font-display font-extrabold text-3xl md:text-5xl text-ink tracking-tight mb-3">Datos de pago</h1>
                        <p className="text-mut mb-8 max-w-2xl">Estas instrucciones se muestran al cliente al confirmar su pedido, según el método que elija. Edítalas con tus datos reales (Pago Móvil, Zelle, Binance, cuenta bancaria, dirección de tienda).</p>
                        {payments && (
                            <div className="space-y-4 max-w-3xl">
                                {PAY_ORDER.map((k) => (
                                    <div key={k} className="border border-linea rounded-2xl bg-hueso p-5 shadow-soft" data-testid={`pay-${k}`}>
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-display font-bold text-lg text-ink">{PAY_LABELS[k]}</h3>
                                            <label className="flex items-center gap-2 text-xs text-mut">
                                                <input type="checkbox" checked={payments[k]?.activo ?? true} onChange={(e) => setPayments((p) => ({ ...p, [k]: { ...p[k], activo: e.target.checked } }))} /> Activo
                                            </label>
                                        </div>
                                        <textarea
                                            data-testid={`pay-input-${k}`}
                                            className="w-full bg-crema border border-linea focus:border-madera rounded-lg outline-none py-2.5 px-3 text-sm min-h-28"
                                            value={payments[k]?.instrucciones || ""}
                                            onChange={(e) => setPayments((p) => ({ ...p, [k]: { ...p[k], instrucciones: e.target.value } }))}
                                            placeholder="Escribe aquí los datos e instrucciones de pago…"
                                        />
                                    </div>
                                ))}
                                <button onClick={savePayments} disabled={savingPay} data-testid="save-payments" className="bg-verde text-white rounded-full px-8 py-3.5 font-semibold hover:bg-verde-800 transition-colors flex items-center gap-2 disabled:opacity-60">
                                    {savingPay ? <Loader2 size={16} className="animate-spin" /> : "Guardar datos de pago"}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {tab === "config" && (
                    <div>
                        <h1 className="font-display font-extrabold text-3xl md:text-5xl text-ink tracking-tight mb-3">Configuración</h1>
                        <p className="text-mut mb-8 max-w-2xl">Datos de contacto del negocio. El <b>WhatsApp</b> se usa en todos los botones del sitio. Si lo dejas vacío, esos botones abrirán el formulario de presupuesto en vez de WhatsApp.</p>
                        {general && (
                            <div className="space-y-5 max-w-2xl">
                                {!(general.whatsapp || "").replace(/\D/g, "") && (
                                    <div className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-800" data-testid="config-wa-warning">
                                        <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                                        <p className="text-sm">Aún no hay número de WhatsApp. Los botones "Consultar por WhatsApp" abrirán el formulario de presupuesto (cero enlaces muertos).</p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-mut font-semibold">WhatsApp (con código de país, solo números)</label>
                                    <input data-testid="config-whatsapp" className="w-full mt-2 bg-hueso border border-linea focus:border-madera rounded-lg outline-none py-3 px-3 text-sm" placeholder="Ej: 584121234567" value={general.whatsapp || ""} onChange={(e) => setGeneral((g) => ({ ...g, whatsapp: e.target.value }))} />
                                    <p className="text-[11px] text-mut mt-1.5">Formato internacional sin "+", espacios ni guiones. Venezuela: 58 + número sin el 0 inicial.</p>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-mut font-semibold">Correos que reciben los avisos</label>
                                    <input data-testid="config-email" className="w-full mt-2 bg-hueso border border-linea focus:border-madera rounded-lg outline-none py-3 px-3 text-sm" placeholder="info@laromanilla.com, otro@ejemplo.com" value={general.email || ""} onChange={(e) => setGeneral((g) => ({ ...g, email: e.target.value }))} />
                                    <p className="text-[11px] text-mut mt-1.5">Avisos de solicitudes de presupuesto y de proyectos nuevos. Puedes poner varias direcciones separadas por coma.</p>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-mut font-semibold">Dirección</label>
                                    <input data-testid="config-direccion" className="w-full mt-2 bg-hueso border border-linea focus:border-madera rounded-lg outline-none py-3 px-3 text-sm" placeholder="Ciudad, estado, referencia…" value={general.direccion || ""} onChange={(e) => setGeneral((g) => ({ ...g, direccion: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-mut font-semibold">Instagram</label>
                                    <input data-testid="config-instagram" className="w-full mt-2 bg-hueso border border-linea focus:border-madera rounded-lg outline-none py-3 px-3 text-sm" placeholder="@maderaslaromanilla" value={general.instagram || ""} onChange={(e) => setGeneral((g) => ({ ...g, instagram: e.target.value }))} />
                                </div>
                                <button onClick={saveGeneral} disabled={savingGen} data-testid="save-config" className="bg-verde text-white rounded-full px-8 py-3.5 font-semibold hover:bg-verde-800 transition-colors flex items-center gap-2 disabled:opacity-60">
                                    {savingGen ? <Loader2 size={16} className="animate-spin" /> : "Guardar configuración"}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {tab === "productos" && (
                    <div>
                        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                            <h1 className="font-display font-extrabold text-3xl md:text-5xl text-ink tracking-tight">Productos</h1>
                            <button onClick={() => setEditing(emptyProduct)} data-testid="admin-new-product" className="flex items-center gap-2 bg-verde text-white rounded-full px-5 py-3 font-medium hover:bg-verde-800 transition-colors">
                                <Plus size={16} /> Nuevo
                            </button>
                        </div>
                        <div className="relative max-w-md mb-6">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-mut" />
                            <input value={q} onChange={(e) => { setQ(e.target.value); setPPage(1); }} placeholder="Buscar producto…" data-testid="admin-product-search" className="w-full bg-hueso border border-linea focus:border-madera rounded-full outline-none py-2.5 pl-10 pr-3 text-sm" />
                        </div>
                        <div className="border border-linea rounded-2xl bg-hueso overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-secondary text-[10px] uppercase tracking-widest text-mut">
                                    <tr><th className="p-3">Producto</th><th className="p-3 hidden md:table-cell">Categoría</th><th className="p-3">Precio</th><th className="p-3 text-right">Acciones</th></tr>
                                </thead>
                                <tbody className="divide-y divide-linea">
                                    {prodData.items.map((p) => (
                                        <tr key={p.id} data-testid="admin-product-row">
                                            <td className="p-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-10 w-10 bg-white rounded-lg shrink-0 overflow-hidden"><ProductImage src={p.foto_url} alt="" className="h-full w-full object-contain p-0.5" /></div>
                                                    <div className="min-w-0"><p className="text-sm line-clamp-1">{p.nombre}</p><p className="text-[10px] text-mut">{p.sku}</p></div>
                                                </div>
                                            </td>
                                            <td className="p-3 hidden md:table-cell text-xs text-mut">{p.categoria}</td>
                                            <td className="p-3 text-sm font-medium">{fmt(p.precio_usd)}</td>
                                            <td className="p-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button onClick={() => setEditing(p)} data-testid={`edit-${p.id}`} className="p-2 text-mut hover:text-madera-dark"><Pencil size={16} /></button>
                                                    <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="p-2 text-mut hover:text-destructive"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex items-center justify-between mt-6">
                            <span className="text-xs text-mut">{prodData.total} productos</span>
                            <div className="flex items-center gap-2">
                                <button disabled={pPage <= 1} onClick={() => setPPage((p) => p - 1)} className="h-9 w-9 rounded-full flex items-center justify-center border border-verde text-verde disabled:opacity-25 hover:bg-verde hover:text-white transition-colors"><ChevronLeft size={16} /></button>
                                <span className="text-sm px-2">{prodData.page} / {prodData.pages}</span>
                                <button disabled={pPage >= prodData.pages} onClick={() => setPPage((p) => p + 1)} className="h-9 w-9 rounded-full flex items-center justify-center border border-verde text-verde disabled:opacity-25 hover:bg-verde hover:text-white transition-colors"><ChevronRight size={16} /></button>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {editing && <ProductModal product={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { loadProducts(); loadStats(); }} />}

            {/* Comprobantes de pago que subió el cliente desde su seguimiento */}
            {comprobantes && (
                <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4" onClick={() => setComprobantes(null)}>
                    <div className="bg-hueso rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-5 border-b border-linea sticky top-0 bg-hueso">
                            <div>
                                <h3 className="font-display font-bold text-lg text-ink">Comprobantes · {comprobantes.order.order_number}</h3>
                                <p className="text-xs text-mut">{comprobantes.order.cliente?.nombre} · {fmt(comprobantes.order.total)}</p>
                            </div>
                            <button onClick={() => setComprobantes(null)} className="text-mut hover:text-ink"><X size={20} /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            {comprobantes.files.map((c, i) => {
                                const esPdf = (c.nombre || "").toLowerCase().endsWith(".pdf") || (c.url || "").toLowerCase().endsWith(".pdf");
                                return (
                                    <div key={i} className="border border-linea rounded-xl overflow-hidden bg-white">
                                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-linea">
                                            <span className="text-xs text-mut">{c.fecha ? new Date(c.fecha).toLocaleString("es-VE") : ""}</span>
                                            <a href={`${API.replace("/api", "")}${c.url}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-verde hover:text-naranja">Abrir ↗</a>
                                        </div>
                                        {esPdf ? (
                                            <p className="p-6 text-sm text-mut text-center">Comprobante en PDF — ábrelo con el enlace de arriba.</p>
                                        ) : (
                                            <img src={`${API.replace("/api", "")}${c.url}`} alt={`Comprobante ${i + 1}`} className="w-full max-h-[55vh] object-contain bg-crema" />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="p-5 border-t border-linea flex gap-3">
                            <button onClick={() => { setStatus(comprobantes.order.id, "pagado"); setComprobantes(null); }}
                                data-testid="confirmar-pago"
                                className="flex-1 bg-verde text-white rounded-full py-3 text-sm font-bold hover:bg-verde-800 transition-colors">
                                Confirmar pago recibido
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
